import React, {useState, useCallback, useMemo, useEffect} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {bindActionCreators} from "redux";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {openDrawer, publish, changeDate} from '../../actions/drawerActions';
import {QD_URL} from "../../constants/systemConstants";
import {getQuestionnaireExport} from "../../actions/questionnaireTemplateActions";
import {initGetTemplateList} from "../../actions/templateListActions";
import QuestionnaireRetireInActiveDrawer, {questionnaireRetireInActiveDrawerProps} from "../drawers/QuestionnaireRetireInActiveDrawerHook";
import QuestionnairePublishOrChangeDateDrawerHook, {questionnairePublishOrChangeDateDrawerProps} from "../drawers/QuestionnairePublishOrChangeDateDrawerHook";
import QuestionnaireNoteHistoryDrawer, {questionnaireNoteHistoryDrawerProps} from "../drawers/QuestionnaireNoteHistoryDrawerHook";
import QuestionnairePortalPreviewDrawer, {questionnairePortalPreviewDrawerProps} from "../drawers/QuestionnairePortalPreviewDrawerHook";
import QuestionnaireSampleSurveyDrawerHook, {questionnaireSampleSurveyDrawerProps} from "../drawers/QuestionnaireSampleSurveyDrawerHook";
import QuestionnaireStatisticsDrawer, {questionnaireStatisticsDrawerProps} from "../drawers/QuestionnaireStatisticsDrawerHook";
import QuestionnaireDeleteDraftDrawer, {questionnaireDeleteDraftDrawerProps} from "../drawers/QuestionnaireDeleteDraftDrawerHook";
import * as TEMPLATE from "../../constants/templateConstants";
import ClickAwayListener from '@mui/material/ClickAwayListener';

const GridActions = (props) => {
    const {
        row,
        link,
        templateType,
        templateMode,
        customActions,
        defaultActions,
        setLoadingState,
        savePortalReferenceJson,
        fileName
    } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const versionsFilterState = useSelector(state => state.filter[TEMPLATE.FILTER[TEMPLATE.MODE.VERSIONS][templateType]]);

    const {
        openDrawer: boundOpenDrawer,
        getQuestionnaireExport: boundGetQuestionnaireExport,
        initGetTemplateList: boundInitGetTemplateList,
        publish: boundPublish,
        changeDate: boundChangeDate
    } = useMemo(() => bindActionCreators({
        openDrawer,
        getQuestionnaireExport,
        initGetTemplateList,
        publish,
        changeDate
    }, dispatch), [dispatch]);

    const [menuState, setMenuState] = useState({
        anchorEl: null,
        menuOpen: false,
        menuItemList: []
    });

    const handleIconButtonClick = useCallback((event) => {
        setMenuState(prevState => ({...prevState, anchorEl: event.currentTarget, menuOpen: true}));
    }, []);

    const handleClickAway = useCallback(() => {
        setMenuState(prevState => ({...prevState, menuOpen: false}));
    }, []);

    const openQuestionnaireRetireInActiveDrawer = useCallback(() => {
        boundOpenDrawer(questionnaireRetireInActiveDrawerProps, <QuestionnaireRetireInActiveDrawer questionnaireRow={row}/>, row);
    }, [boundOpenDrawer, row]);

    const openQuestionnaireDeleteDraftDrawer = useCallback(() => {
        boundOpenDrawer(questionnaireDeleteDraftDrawerProps, <QuestionnaireDeleteDraftDrawer/>, row);
    }, [boundOpenDrawer, row]);

    const openQuestionnairePublishDateDrawer = useCallback(() => {
        boundOpenDrawer(
            questionnairePublishOrChangeDateDrawerProps,
            <QuestionnairePublishOrChangeDateDrawerHook
                mode="Publish:"
                title={row.name}
                qualifier={row.nameQualifier}
                buttonLabel="PUBLISH"
                template={row}
                currentStartDate=""
                action={boundPublish}
                setLoadingState={setLoadingState}
            />,
            row
        );
    }, [boundOpenDrawer, row, boundPublish, setLoadingState]);

    const openQuestionnaireChangeDateDrawer = useCallback(() => {
        boundOpenDrawer(
            questionnairePublishOrChangeDateDrawerProps,
            <QuestionnairePublishOrChangeDateDrawerHook
                mode="Change Date:"
                title={row.name}
                qualifier={row.nameQualifier}
                buttonLabel="SUBMIT"
                action={boundChangeDate}
                startDate={row.startDate}
                template={row}
                endDate={row.endDate}
                expirationDays={row.expirationDays}
                expirationDescription={row.expirationDescription}
                displayShortForm={row.displayShortForm}
                currentStartDate=""
                setLoadingState={setLoadingState}
            />,
            row
        );
    }, [boundOpenDrawer, row, boundChangeDate, setLoadingState]);

    const openVersions = useCallback((key, name, nameQualifier) => {
        boundInitGetTemplateList(versionsFilterState, templateType, TEMPLATE.MODE.VERSIONS, key, name, nameQualifier, defaultActions);
        const path = QD_URL + "/" + templateType + "-versions/" + key;
        navigate(path, {replace: true});
    }, [boundInitGetTemplateList, navigate, versionsFilterState, templateType, defaultActions]);

    const openQuestionnaireNoteHistoryDrawer = useCallback(() => {
        boundOpenDrawer(questionnaireNoteHistoryDrawerProps, <QuestionnaireNoteHistoryDrawer templateId={fileName === "TemplateVersionsList" ? row.id : row.templateKey}/>, row);
    }, [fileName, boundOpenDrawer, row]);

    const openQuestionnairePortalPreviewDrawer = useCallback(() => {
        boundOpenDrawer(questionnairePortalPreviewDrawerProps, <QuestionnairePortalPreviewDrawer templateId={row.id}/>, row);
    }, [boundOpenDrawer, row]);

    const openQuestionnaireSampleSurveyDrawer = useCallback(() => {
        boundOpenDrawer(
            questionnaireSampleSurveyDrawerProps,
            <QuestionnaireSampleSurveyDrawerHook
                templateId={row.id}
                savePortalReferenceJson={savePortalReferenceJson}
                versionsFilter={versionsFilterState}
                templateType={templateType}
                defaultActions={defaultActions}
                fileName={fileName}
            />,
            row
        );
    }, [boundOpenDrawer, row, savePortalReferenceJson, versionsFilterState, templateType, defaultActions, fileName]);

    const openQuestionnaireStatisticsDrawer = useCallback(() => {
        boundOpenDrawer(questionnaireStatisticsDrawerProps, <QuestionnaireStatisticsDrawer templateId={row.id} templateKey={row.templateKey}/>, row);
    }, [boundOpenDrawer, row]);

    const exportQuestionnaire = useCallback(() => {
        boundGetQuestionnaireExport(row);
    }, [boundGetQuestionnaireExport, row]);

    const getIsRetireDisabled = useCallback((rowData) => {
        return !!rowData.isActive;
    }, []);

    //currently we are not disabling retire, we just don't add it to the menu
    const getRetireToolTip = useCallback((rowData) => {
        return getIsRetireDisabled(rowData) ? "Only published expired questionnaires can be retired." : "";
    }, [getIsRetireDisabled]);

    const getIsEditDisabled = useCallback((label, rowData) => {
        let isDisabled = false;

        if (label === "Edit") {
            if (props.openVersions) {
                isDisabled = (rowData.currentFlag === false);
            }
            else {
                isDisabled = (rowData.isEditable === false);
            }
        }

        return isDisabled;
    }, [props.openVersions]);

    const viewIntegrationLogs = useCallback((id) => {
        const path = QD_URL + "/integration-logs/" + id;
        navigate(path, {replace: true});
    }, [navigate]);

    const getEditDisabledToolTip = useCallback((label, rowData) => {
        const showTooltip = getIsEditDisabled(label, rowData);
        let tooltipText = "";

        if (showTooltip) {
            tooltipText = `A current draft for this ${templateType} already exists and any changes should be made to this draft`;
        }

        return tooltipText;
    }, [getIsEditDisabled, templateType]);

    const actionFunction = useCallback((value, linkArg, index, rowData) => {
        switch (value) {
            case "Publish":
                return <MenuItem key={'publish'} onClick={openQuestionnairePublishDateDrawer}>Publish</MenuItem>;
            case "Retire":
                if (!rowData.isActive && props.fileName === "TemplateVersionsList") return <MenuItem key={'retire'}
                                 onClick={openQuestionnaireRetireInActiveDrawer}
                                 disabled={getIsRetireDisabled(rowData)}
                                 title={getRetireToolTip(rowData)}
                >Retire</MenuItem>;
            break;
            case "Delete":
                return <MenuItem key={'deleteDraft'} onClick={openQuestionnaireDeleteDraftDrawer}>Delete Draft</MenuItem>;
            case "Change Effective Dates":
                return <MenuItem key={'changeDate'} onClick={openQuestionnaireChangeDateDrawer}>Change Start/End Date</MenuItem>;
            default:
        }
    }, [openQuestionnairePublishDateDrawer, openQuestionnaireRetireInActiveDrawer, getIsRetireDisabled, getRetireToolTip, openQuestionnaireDeleteDraftDrawer, openQuestionnaireChangeDateDrawer, props.fileName]);

    const menu = [];

    if (customActions) {
        const uniqId = ((templateType === TEMPLATE.TYPE.INTEGRATION) || (templateType === TEMPLATE.TYPE.TAG)) ? row.id : row.templateKey;
        const questionnaireType = (templateType === TEMPLATE.TYPE.QUESTIONNAIRE) ? row.questionnaireType : "";

        customActions.map((action, index) => {
            if (!action || typeof action.func !== "function") {
                return false;
            }
            if (action.label === 'Review') {
                menu.push(
                    <MenuItem key={'customAction' + index}
                              onClick={action.func.bind(null, uniqId, row.id.toString(), row.name, row, {altKey: false, ctrlKey: false})}
                              title={getEditDisabledToolTip(action.label, row)}
                              disabled={getIsEditDisabled(action.label, row)}>
                        {(templateMode === "versions" && getIsEditDisabled(action.label, row)) ? "Edit" : action.label}
                    </MenuItem>
                );
            }
            else {
                menu.push(
                    <MenuItem key={'customAction' + index}
                              onClick={action.func.bind(null, uniqId, row.id, templateMode, questionnaireType, row)}
                              title={getEditDisabledToolTip(action.label, row)}
                              disabled={getIsEditDisabled(action.label, row)}>
                        {(templateMode === "versions" && getIsEditDisabled(action.label, row)) ? "Edit" : action.label}
                    </MenuItem>
                );
            }
            return false;
        });
    }

    switch (templateType) {
        case TEMPLATE.TYPE.QUESTIONNAIRE:
            if (row.status) {
                menu.push(row.status.actions.map((value, index) => {
                    return actionFunction(value, link + row.templateKey, index, row);
                }));

                if (templateMode === TEMPLATE.MODE.CURRENT) {
                    menu.push(
                        <MenuItem key={'versions'} onClick={openVersions.bind(null, row.templateKey, row.name, row.nameQualifier)}>Versions</MenuItem>,
                    );
                }

                const todaysDate = new Date().toJSON().slice(0, 10);

                if (row.status.name !== "Retired") {
                    if ((row.status.name === "Draft") || (row.isActive === undefined) || (row.isActive === true) || (todaysDate < row.startDate)) {
                        menu.push(<MenuItem key={'createRef'} onClick={openQuestionnaireSampleSurveyDrawer}>Create Sample Survey</MenuItem>);
                    }
                }

                menu.push(
                    <MenuItem key={'noteHistory'} onClick={openQuestionnaireNoteHistoryDrawer}>Note History</MenuItem>,
                    <MenuItem key={'portalPreview'} onClick={openQuestionnairePortalPreviewDrawer}>Portal Preview</MenuItem>,
                    <MenuItem key={'questionnaireStatistics'} onClick={openQuestionnaireStatisticsDrawer}>Survey Statistics</MenuItem>,
                    <MenuItem key={'export'} onClick={exportQuestionnaire}>Export Questionnaire</MenuItem>
                );

            }
            break;

        case TEMPLATE.TYPE.CATEGORY:
        case TEMPLATE.TYPE.GROUP:
        case TEMPLATE.TYPE.QUESTION:
        case TEMPLATE.TYPE.ACTION:

            if (templateMode === TEMPLATE.MODE.CURRENT) {
                menu.push(
                    <MenuItem key={'versions'} onClick={openVersions.bind(null, row.templateKey, row.name, row.nameQualifier)}>Versions</MenuItem>,
                );
            }
            break;

        case TEMPLATE.TYPE.INTEGRATION:
            menu.push(
                <MenuItem key={'logs'} onClick={viewIntegrationLogs.bind(null, row.id)}>Logs</MenuItem>
            );
            break;
        case TEMPLATE.TYPE.INTEGRATION_LOGS:
            break;
        default:
            break;
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                handleClickAway()
            }
        }

        document.addEventListener("keydown", handleKeyDown, true)

        return () => {document.removeEventListener("keydown", handleKeyDown, true)}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (menuState.menuItemList.lenght === 0 ? <div></div> :
            <div id="grid-actions-icon-button-container-div">
                <div id="grid-actions-icon-button-div">
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <IconButton id={"grid-actions-icon-button" + row.id} name={"grid-actions-icon-button" + row.id} onClick={handleIconButtonClick}>
                            <MoreVertIcon />
                        </IconButton>
                    </ClickAwayListener>
                </div>

                <Menu
                    id="lock-menu"
                    anchorEl={menuState.anchorEl}
                    open={menuState.menuOpen}

                    MenuListProps={{
                        'aria-labelledby': 'lock-button',
                        role: 'listbox',
                    }}
                >
                    {menu.flat()}
                </Menu>
            </div>
    );
};

GridActions.defaultProps = {
    fileName: ""
};

GridActions.propTypes = {
    gridListType: PropTypes.string,
    row: PropTypes.object,
    link: PropTypes.string,
    customActions: PropTypes.array,
    type: PropTypes.string,
    integrationLogsFilter: PropTypes.object,
    templateType: PropTypes.string,
    templateMode: PropTypes.string,
    addVersionsAction: PropTypes.bool,
    versionsFilter: PropTypes.object,
    defaultActions: PropTypes.object,
    setLoadingState: PropTypes.func,
    savePortalReferenceJson: PropTypes.func,
    viewIntegrationLogs: PropTypes.func,
    fileName: PropTypes.string
};

export default GridActions;
